// PLZ-304: tarjeta de sellos visible en la App Web (portada y seguimiento). Maqueta aprobada por Yunes el 17 oct.
// Todo lo calcula el backend (sellos, valor estimado desde el 2.o sello); aqui solo se pinta.
// El color sale del comercio (colorBoton); lo demas es neutro para todos los comercios.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchLoyalty, formatCOP, formatFecha, luminance } from "../../utils/loyalty";
import "./TarjetaSellos.css";

const IconGift = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5" />
  </svg>
);

// Sin emojis (norma del proyecto): iconos SVG del mismo trazo que el resto de la App Web.
const IconAward = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="6" />
    <path d="M15.5 13.2 17 22l-5-3-5 3 1.5-8.8" />
  </svg>
);

const IconMessage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.6 8.6 0 0 1-3.8-.9L3 20.5l1.6-4.6A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12l5 5L20 7" />
  </svg>
);

// Oscurece un color hex para el degradado de la tarjeta. Si no es hex, usa el morado de Sealky.
function darken(hex, amount = 0.28) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return "#1b1640";
  const n = parseInt(m[1], 16);
  const c = (v) => Math.max(0, Math.round(v * (1 - amount)));
  const r = c((n >> 16) & 255), g = c((n >> 8) & 255), b = c(n & 255);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

// Color oscuro del comercio: la tarjeta va de ese color con texto blanco.
// Color claro (amarillo, naranja claro…): tarjeta oscura neutra y el color del comercio como acento.
function cardTheme(brand) {
  if (luminance(brand) > 0.35) {
    return {
      "--ts-bg1": "#26303d", "--ts-bg2": "#111827",
      "--ts-on-bg": brand, "--ts-on-fg": "#111827",
      "--ts-cta-bg": brand, "--ts-cta-fg": "#111827",
    };
  }
  return {
    "--ts-bg1": brand, "--ts-bg2": darken(brand),
    "--ts-on-bg": "#ffffff", "--ts-on-fg": brand,
    "--ts-cta-bg": "#ffffff", "--ts-cta-fg": brand,
  };
}

function brandColor() {
  try {
    const v = localStorage.getItem("colorBoton");
    if (v && /^#?[0-9a-f]{6}$/i.test(v.trim())) return v.trim().startsWith("#") ? v.trim() : `#${v.trim()}`;
  } catch (e) { /* sin acceso al almacenamiento */ }
  return "#29225B";
}

// serviceId: pedido del cliente (seguimiento) o el ultimo guardado en el telefono (portada).
// compact: seguimiento; solo tarjetas con sellos o premio.
export default function TarjetaSellos({ serviceId, compact = false }) {
  const [loyalty, setLoyalty] = useState(null);
  const [openTerms, setOpenTerms] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    fetchLoyalty(serviceId)
      .then((d) => alive && setLoyalty(d))
      .catch(() => alive && setLoyalty(null));
    return () => {
      alive = false;
    };
  }, [serviceId]);

  if (!loyalty) return null;
  const cards = loyalty.cards ?? [];
  const rewards = loyalty.rewards ?? [];
  const visibles = compact ? cards.filter((c) => Number(c.stamps) > 0) : cards;
  if (visibles.length === 0 && rewards.length === 0) return null;

  const brand = brandColor();
  const style = cardTheme(brand);

  // "Pedir ahora": en la portada baja a los botones de pedido; en otra pantalla vuelve a la portada.
  const pedir = () => {
    if (location.pathname === "/") {
      const el = document.querySelector(".col-md-6.button");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      navigate("/");
    }
  };

  const termsFor = (c) =>
    c.terms ? (
      <>
        <button type="button" className="ts-terms-btn" onClick={() => setOpenTerms(openTerms === c.id ? null : c.id)}>
          {openTerms === c.id ? "Ocultar términos" : "Términos y condiciones"}
        </button>
        {openTerms === c.id && <p className="ts-terms">{c.terms}</p>}
      </>
    ) : null;

  return (
    <div className="ts-wrap" style={style}>
      {rewards.length > 0 && (
        <div className="ts-card ts-win">
          <div className="ts-top">
            <span className="ts-tag"><IconAward /> ¡Ganaste!</span>
            {rewards.length > 1 && <span className="ts-more">+{rewards.length - 1} más</span>}
          </div>
          <div className="ts-prize">{formatCOP(rewards[0].value)}</div>
          <p className="ts-sub ts-tight">de descuento en tu próximo pedido · vence el {formatFecha(rewards[0].expires_at)}</p>
          <div className="ts-hint"><IconMessage /><span>Tu código te llegó por WhatsApp. Escríbelo al pagar tu pedido, o dilo al pedir por teléfono o en el local.</span></div>
        </div>
      )}

      {visibles.map((c) => {
        const n = Math.max(1, Math.min(20, Number(c.units_required) || 0));
        const stamps = Math.min(n, Number(c.stamps) || 0);
        const missing = Math.max(0, n - stamps);
        return (
          <div className="ts-card" key={c.id}>
            <div className="ts-top">
              <span className="ts-tag"><IconGift /> {c.name}</span>
              <span className="ts-count">
                {stamps}
                <small>/{n}</small>
              </span>
            </div>
            <div className="ts-headline">
              {stamps === 0 ? (
                <>
                  Pide {n} {n === 1 ? "vez" : "veces"} y <span>gana uno GRATIS</span>
                </>
              ) : missing === 1 ? (
                <>
                  ¡Te falta <span>1 pedido</span> para tu premio!
                </>
              ) : (
                <>
                  Llevas {stamps} de {n}: <span>te faltan {missing}</span>
                </>
              )}
            </div>
            <p className="ts-sub">
              {stamps === 0
                ? "Cada pedido suma un sello. Al completar la tarjeta te llega tu premio por WhatsApp."
                : "Sigue pidiendo y gana uno GRATIS."}
            </p>
            <div className="ts-stamps">
              {Array.from({ length: n }).map((_, i) => {
                const on = i < stamps;
                const last = i === n - 1;
                return (
                  <span key={i} className={`ts-st${on ? " on" : ""}${!on && last ? " prize" : ""}`}>
                    {on ? <IconCheck /> : last ? <IconGift /> : null}
                  </span>
                );
              })}
            </div>
            {c.estimated_value !== null && c.estimated_value !== undefined && (
              <div className="ts-estimate">
                <span>A este ritmo ganarías</span>
                <b>{formatCOP(c.estimated_value)}</b>
              </div>
            )}
            <button type="button" className="ts-cta" onClick={pedir}>
              {stamps === 0 && !loyalty.known_customer ? "Haz tu primer pedido" : "Pedir ahora"}
            </button>
            {!compact && termsFor(c)}
          </div>
        );
      })}
    </div>
  );
}
